import { Navigate, useParams } from 'react-router-dom';
import { growthApi, projectApi } from '@shared/api-client';
import type { GrowthFeedback, GrowthPlan } from '@shared/api-client/growth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/hooks/use-session';
import { toErrorMessage } from '@/hooks/use-form-errors';
import { OpportunitiesView } from './view';
import { useAssistantPageContext } from '@/components/assistant/assistant-context';

export const OpportunitiesPage = () => {
  const { projectId = '' } = useParams();
  const { user } = useSession();
  const client = useQueryClient();
  const canManage = ['owner', 'admin'].includes(
    user?.currentOrganization.role ?? '',
  );
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.getProject(projectId),
    enabled: Boolean(projectId),
  });
  useAssistantPageContext({
    page: 'Opportunities',
    projectId,
    projectName: project.data?.name ?? 'Project',
    range: '7d',
    rangeLabel: 'Last 7 days',
  });
  const query = useQuery({
    queryKey: ['growth', projectId],
    queryFn: () => growthApi.getGrowth(projectId),
    enabled: Boolean(project.data),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const plan = useMutation({
    mutationFn: (input: GrowthPlan) => growthApi.savePlan(projectId, input),
    onSuccess: (data) => client.setQueryData(['growth', projectId], data),
  });
  const feedback = useMutation({
    mutationFn: (input: { id: string; feedback: GrowthFeedback }) =>
      growthApi.saveFeedback(projectId, input.id, input.feedback),
    onSuccess: (data) => client.setQueryData(['growth', projectId], data),
  });
  if (canManage && project.data?.profile?.setupStep === 'identity') {
    return <Navigate to={`/projects/${projectId}/setup`} replace />;
  }
  return (
    <OpportunitiesView
      key={projectId}
      data={query.data}
      projectId={projectId}
      projectName={project.data?.name ?? 'your product'}
      needsProfile={Boolean(
        project.data &&
        !project.data.primaryDomain &&
        !project.data.profile?.websiteUrl &&
        (project.data.profile?.description?.trim().length ?? 0) <= 20,
      )}
      canManage={canManage}
      loading={project.isLoading || query.isLoading}
      error={
        project.error || query.error
          ? toErrorMessage(project.error ?? query.error)
          : undefined
      }
      onRetry={() => {
        void project.refetch();
        void query.refetch();
      }}
      onPlan={async (input) => {
        await plan.mutateAsync(input);
      }}
      onFeedback={(id, value) => feedback.mutate({ id, feedback: value })}
      savingPlan={plan.isPending}
      savingId={feedback.isPending ? feedback.variables?.id : undefined}
      planError={plan.error ? toErrorMessage(plan.error) : undefined}
      feedbackError={
        feedback.error
          ? {
              id: feedback.variables.id,
              message: toErrorMessage(feedback.error),
            }
          : undefined
      }
    />
  );
};
